import { Stack, type StackProps } from '@mui/material'
import { IconContext, XIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { ReactNode, useState } from 'react'
import { IconButton } from '../../buttons'

type InfoBoxProps = Omit<StackProps, 'children'> & {
    variant: 'warning' | 'ordinary' | 'positive' | 'negative' | 'neutral' | 'external'
    icon?: ReactNode
    size?: 'small' | 'medium'
    showCloseButton?: boolean
    closeButtonLabel?: string
    children: ReactNode
}

export function InfoBox({
    variant,
    icon,
    size = 'medium',
    showCloseButton = false,
    closeButtonLabel = 'Close',
    children,
    ...stackProps
}: InfoBoxProps) {
    const [isVisible, setIsVisible] = useState(true)

    const backgroundColor = {
        warning: tokens.ColorSurfaceWarningDefault,
        ordinary: tokens.ColorSurfaceInformativeDefault,
        positive: tokens.ColorSurfacePositiveDefault,
        negative: tokens.ColorSurfaceDangerDefault,
        neutral: tokens.ColorSurfaceAccentGreyDefault,
        external: tokens.ColorGraphSequentialThreeLightest,
    }
    const iconColor = {
        warning: tokens.ColorIconWarning,
        ordinary: tokens.ColorIconInformative,
        positive: tokens.ColorIconPositive,
        negative: tokens.ColorIconDanger,
        neutral: tokens.ColorIconAccentGrey,
        external: tokens.ColorGraphSequentialThreeMedium,
    }
    const borderColor = {
        warning: tokens.ColorBorderWarning,
        ordinary: tokens.ColorBorderInformative,
        positive: tokens.ColorBorderPositive,
        negative: tokens.ColorBorderDanger,
        neutral: tokens.ColorBorderAccentGrey,
        external: tokens.ColorGraphSequentialThreeMedium,
    }

    if (!isVisible) {
        return null
    }

    return (
        <Stack
            direction="row"
            gap={tokens.SizingSpacing100}
            padding={tokens.SizingSpacing100}
            bgcolor={backgroundColor[variant]}
            borderRadius={tokens.SizingCornerM}
            border={`${tokens.SizingBorderS} solid ${borderColor[variant]}`}
            sx={size === 'small' ? tokens.TextXs : tokens.TextS}
            {...stackProps}
        >
            {icon && (
                <IconContext.Provider
                    value={{
                        size: size === 'small' ? tokens.SizingIconS : tokens.SizingIconM,
                        color: iconColor[variant],
                        weight: size === 'small' ? 'bold' : 'regular',
                    }}
                >
                    <Stack sx={{ flexShrink: 0 }}>{icon}</Stack>
                </IconContext.Provider>
            )}
            {children}
            {showCloseButton && (
                <Stack marginLeft="auto" alignSelf="flex-start" flexShrink={0}>
                    <IconButton
                        size="small"
                        variant="tertiary"
                        icon={<XIcon />}
                        ariaLabel={closeButtonLabel}
                        tooltip={closeButtonLabel}
                        onClick={() => setIsVisible(false)}
                    />
                </Stack>
            )}
        </Stack>
    )
}
